import React, { useState } from 'react'
import classNames from 'classnames'
import { NavLink, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import ReactSVG from 'react-svg'

import { toggleAccountNavVisibility } from '../../../actions'
import { useIsAuthenticated } from '../../../hooks/use-is-authenticated'
import { useIsKid } from '../../../hooks/use-is-kid'
import { STATIC_SCREENS } from '../../../constants'
import { E2E_SELECTORS } from '../../../e2e-selectors'
import notificationIcon from '../../../../images/notification.svg'
import AccountNavButton from './account-nav-button'
import LoginNavButton from './login-nav-button'
import SignupNavButton from './signup-nav-button'
import NavigationAccount from './navigation-account'
import SearchNavButton from './search-nav-button'
import ChromecastNavButton from './chromecast-nav-button'
import styles from './navigation-right.css'

/**
 * NavigationRight is the shared component responsible for rendering
 * all right-side navigation buttons in the header.
 * It handles the top-right menu for both authenticated and guest users.
 *
 *  * Menu structure:
 * 1. SearchNavButton – search button
 * 2. ChromecastNavButton – cast button
 * 3. Notifications link – bell icon linking to the notification hub (logged-in non-kids users)
 * 4. AccountNavButton – avatar + dropdown menu for logged-in users
 * 5. LoginNavButton – login button for guests
 * 6. SignupNavButton – signup button for guests (hidden on homepage)
 * 7. NavigationAccount – dropdown menu with account-related links and actions:
 *   My List, My Rentals, My Account, Manage Profiles, Help, Log Out, Switch Profile
 */
function NavigationRight() {
  const location = useLocation()
  const isKidProfile = useIsKid()
  const isHomePage = location.pathname === '/'

  const dispatch = useDispatch()

  const searchIsClicked = useSelector((state) => state.navigation.searchIsClicked)
  const isAuthenticated = useIsAuthenticated()

  // Local state to store account nav link ref
  const [navigationLinkRef, setNavigationLinkRef] = useState(null)

  // Handlers
  const onAccountNavClick = () => {
    if (navigationLinkRef) {
      dispatch(toggleAccountNavVisibility(!navigationLinkRef.isOpen, navigationLinkRef.clientWidth))
    }
  }

  const onRegisterAccountNavigationLink = (ref) => {
    setNavigationLinkRef(ref)
  }

  return (
    <div
      className={classNames({
        [styles.rightNav]: true,
        [styles.searchIsClicked]: searchIsClicked,
        [styles.searchIsClickedAuthenticated]: searchIsClicked && isAuthenticated
      })}
    >
      <SearchNavButton isAuthenticated={isAuthenticated} />
      <ChromecastNavButton
        className={classNames({
          [styles.chromecastNavButton]: true,
          [styles.searchIsOpen]: searchIsClicked
        })}
      />
      {isAuthenticated && !isKidProfile && (
        <NavLink
          exact
          to={STATIC_SCREENS.NOTIFICATIONS.screenId}
          className={classNames(styles.notificationLink, {
            [styles.notificationLinkWhileSearching]: searchIsClicked
          })}
          activeClassName={styles.notificationLinkActive}
          aria-label="Notifications"
          data-e2e={E2E_SELECTORS.NOTIFICATIONS_IN_HEADER}
        >
          <ReactSVG
            src={notificationIcon}
            wrapper="span"
            beforeInjection={svg => {
              svg.setAttribute('class', styles.notificationIcon)
            }}
          />
        </NavLink>
      )}
      {isAuthenticated && (
        <AccountNavButton
          onClick={onAccountNavClick}
          onRegisterAccountNavigationLink={onRegisterAccountNavigationLink}
        />
      )}

      {!isAuthenticated && (
        <>
          <LoginNavButton searchIsClicked={searchIsClicked} location={location} />
          {!isHomePage && <SignupNavButton searchIsClicked={searchIsClicked} />}
        </>
      )}
      <NavigationAccount />
    </div>
  )
}

export default NavigationRight
